import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { GridOverrides } from '@mds/common';
import { Target } from '@mds/ui';
import { finalize, Observable } from 'rxjs';

export enum Period {
  FullYear = '',
  FirstHalfYear = 'half_1',
  SecondHalfYear = 'half_2',
  Q1 = 'q1',
  Q2 = 'q2',
  Q3 = 'q3',
  Q4 = 'q4'
}

export type TimePeriod = {
  year: number[];
  period: Period;
};

export type CostingSectionReq = {
  company_rid: string;
  time_period?: TimePeriod;
  page?: number;
  limit?: number;
  filters?: {
    [key: string]: string | TimePeriod | number | string[];
  };
};

export type Filters = { key: string; label: string; options: { text: string; value: string }[]; multiselect?: boolean };

export type CostingSummaryRes = {
  currency: string;
  totals: {
    title: string;
    value: string;
    icon: string;
    suffix?: string;
    subtitle?: string;
    image: string;
  }[];
  lastUpdated: string;
  filter_schema: FilterSchema[];
};

export type FilterSchema = { title?: string; icon?: string; filters: Filters[] };

export type CostingHotspotRow = {
  rid: string;
  name: string;
  supplier: string;
  part_number: string;
  mpn: string;
  amount_bought: number;
  spend: string;
  should_cost: string;
  savings_potential: string;
  data_coverage: string;
};

type CostingSectionsRes = {
  icon: string;
  title: string;
  description: string;
  filter_schema?: FilterSchema[];
  targets?: Target[];
  charts?: {
    title: string;
    description: string;
    type?: string;
    options: Highcharts.Options;
  }[];
  rowData?: CostingHotspotRow[];
  overrides?: GridOverrides;
  totalCount?: number;
  page?: number;
};

export type CostingSection = CostingSectionsRes & {
  key: CostingSectionKeys;
};

export enum CostingSectionKeys {
  Summary = 'summary',
  SpendBreakdown = 'spendBreakdown',
  CategoryBreakdown = 'categoryBreakdown',
  SupplierBreakdown = 'supplierBreakdown',
  Hotspots = 'hotspots'
}

export type SectionKey = Exclude<CostingSectionKeys, CostingSectionKeys.Summary>;

@Injectable()
export class CostingService {
  private readonly SECTION_KEYS: SectionKey[] = [
    CostingSectionKeys.SpendBreakdown,
    CostingSectionKeys.CategoryBreakdown,
    CostingSectionKeys.SupplierBreakdown,
    CostingSectionKeys.Hotspots
  ];

  private emptySectionRecord<T>(): Record<SectionKey, T | null> {
    return Object.fromEntries(this.SECTION_KEYS.map((key) => [key, null])) as Record<SectionKey, T | null>;
  }

  private readonly httpClient = inject(HttpClient);

  public readonly summary = signal<CostingSummaryRes | null>(null);
  private readonly sectionConfig: Record<
    SectionKey,
    { endpoint: string; signal: WritableSignal<CostingSection | null> }
  > = {
    [CostingSectionKeys.SpendBreakdown]: { endpoint: 'spend-breakdown', signal: signal<CostingSection | null>(null) },
    [CostingSectionKeys.CategoryBreakdown]: {
      endpoint: 'category-breakdown',
      signal: signal<CostingSection | null>(null)
    },
    [CostingSectionKeys.SupplierBreakdown]: {
      endpoint: 'supplier-breakdown',
      signal: signal<CostingSection | null>(null)
    },
    [CostingSectionKeys.Hotspots]: { endpoint: 'hotspots', signal: signal<CostingSection | null>(null) }
  };
  public readonly allSections = computed(() => Object.values(this.sectionConfig).map((config) => config.signal()));

  private readonly companyRid = signal<string | null>(null);
  private readonly timePeriodFilter = signal<TimePeriod>({ year: [new Date().getFullYear()], period: Period.FullYear });
  public readonly baseReq = computed<CostingSectionReq>(() => {
    return {
      company_rid: this.companyRid(),
      time_period: this.timePeriodFilter()
    };
  });

  private readonly loadingTracker = signal<CostingSectionKeys[]>([]);
  public readonly isLoading = computed(() => this.loadingTracker().length > 0);

  public readonly summaryFiltersSchema = signal<FilterSchema[] | null>(null);
  public readonly sectionFiltersSchema = signal<{ [key in SectionKey]: FilterSchema[] }>(
    this.emptySectionRecord<FilterSchema[]>()
  );
  public readonly activeSectionFilters = signal<{
    [key in SectionKey]: Record<string, string | number | string[]>;
  }>(this.emptySectionRecord<Record<string, string | number | string[]>>());

  public updateCompanyRid(rid: string): void {
    this.companyRid.set(rid);
  }

  public updateTimePeriodFilter(timePeriod: TimePeriod): void {
    this.clearActiveSectionFilters();
    this.timePeriodFilter.set(timePeriod);
  }

  public updateSectionFilter(filters: FilterSchema[], sectionKey: CostingSectionKeys): void {
    this.sectionFiltersSchema.update((val) => {
      return {
        ...val,
        [sectionKey]: filters
      };
    });
  }

  public clearActiveSectionFilters(): void {
    this.activeSectionFilters.set(this.emptySectionRecord<Record<string, string | number | string[]>>());
  }

  public updateActiveSectionFilter(
    filters: Record<string, string | number | string[]>,
    sectionKey: CostingSectionKeys
  ): void {
    this.activeSectionFilters.update((val) => {
      return {
        ...val,
        [sectionKey]: filters
      };
    });
  }

  private initSectionFiltersOnce(sectionKey: CostingSectionKeys, filterSchema: FilterSchema[]): void {
    if (!this.sectionFiltersSchema()[sectionKey]) {
      this.updateSectionFilter(filterSchema, sectionKey);
    }
  }

  private addLoading(key: CostingSectionKeys): void {
    this.loadingTracker.update((arr) => (arr.includes(key) ? arr : [...arr, key]));
  }

  private removeLoading(key: CostingSectionKeys): void {
    this.loadingTracker.update((arr) => arr.filter((k) => k !== key));
  }

  public fetchSummary(payload: CostingSectionReq): void {
    this.addLoading(CostingSectionKeys.Summary);
    this.httpClient
      .post<CostingSummaryRes>(`/api/v1/costing/summary`, payload)
      .pipe(finalize(() => this.removeLoading(CostingSectionKeys.Summary)))
      .subscribe((res) => {
        this.summary.set(res);

        if (!this.summaryFiltersSchema()) {
          this.summaryFiltersSchema.set(res.filter_schema);
        }
      });
  }

  public fetchSection(sectionKey: SectionKey, payload: CostingSectionReq): void {
    const config = this.sectionConfig[sectionKey];
    this.addLoading(sectionKey);
    this.httpClient
      .post<CostingSectionsRes>(`/api/v1/costing/${config.endpoint}`, payload)
      .pipe(finalize(() => this.removeLoading(sectionKey)))
      .subscribe((res) => {
        config.signal.set({ ...res, key: sectionKey });
        this.initSectionFiltersOnce(sectionKey, res.filter_schema);
      });
  }

  public updateHotspotsSection(section: CostingSectionsRes): void {
    const config = this.sectionConfig[CostingSectionKeys.Hotspots];
    config.signal.set({ ...section, key: CostingSectionKeys.Hotspots });
    this.initSectionFiltersOnce(CostingSectionKeys.Hotspots, section.filter_schema);
  }

  public fetchHotspotsRowData(payload: CostingSectionReq): Observable<CostingSectionsRes> {
    return this.httpClient.post<CostingSectionsRes>(
      `/api/v1/costing/${this.sectionConfig[CostingSectionKeys.Hotspots].endpoint}`,
      payload
    );
  }
}
